import { orderXClient } from '@/service/client'
import { Exchange } from '@/types/generated/common/types'
import { ExchangeStatus, ServerMessage, VenueSchedule } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

/**
 * Live venue open/closed status. Public endpoint; the backend computes the
 * status from an embedded session calendar, so boundaries are absolute UTC
 * timestamps — no client-side timezone math.
 */
export async function fetchVenueStatus(exchange: Exchange): Promise<ExchangeStatus> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      getVenueStatus: { exchange },
    })
    const status = data.data?.exchangeStatus
    if (!status) {
      // An error means "status could not be determined" — never infer CLOSED.
      throw new Error(data.error?.message || 'Venue status unavailable')
    }
    return status
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Static weekly "Regular Hours" per venue, in each venue's local timezone.
 * Public endpoint; fetch once per session and cache — it only changes with
 * a backend release.
 */
export async function fetchVenueSchedules(): Promise<VenueSchedule[]> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getVenueSchedules: {} })
    return data.data?.venueSchedules?.schedules ?? []
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}
